import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { Link } from "@/components/LocalizedLink";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/losa-in")({
  head: () => ({
    meta: [
      { title: "Lös in licenskod – Alkoholprovet" },
      { name: "description", content: "Har du fått en licenskod från din arbetsgivare? Lös in den här och få Premium i 30 dagar inför kunskapsprovet." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Lös in licenskod – Alkoholprovet" },
      { property: "og:description", content: "Lös in licenskoden från din arbetsgivare och få Premium i 30 dagar." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RedeemPage,
});

const reasons: Record<string, string> = {
  invalid: "Koden hittades inte. Kontrollera att du skrivit rätt.",
  used: "Koden har redan använts. Be din arbetsgivare om en ny kod.",
  error: "Något gick fel. Försök igen eller mejla info@alkoholprovet.se.",
};

function RedeemPage() {
  const { user, loading } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [until, setUntil] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { redeemLicenseCode } = await import("@/lib/team-licenses.functions");
      const r = await redeemLicenseCode({ data: { code } });
      if (r.ok) setUntil(r.activeUntil);
      else setErr(reasons[r.reason] ?? reasons.error);
    } catch {
      setErr(reasons.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-narrow max-w-md py-14">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
        <KeyRound className="h-5 w-5 text-accent" />
      </span>
      <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">Lös in licenskod</h1>
      <p className="mt-2 text-muted-foreground">Fått en kod från din arbetsgivare? Skriv in den här så får du Premium i 30 dagar.</p>

      {until ? (
        <div className="mt-6 rounded-2xl border border-success/30 bg-success/10 p-5">
          <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-5 w-5 text-success" /> Premium är aktiverat!</div>
          <p className="mt-1 text-sm text-muted-foreground">Gäller till {new Date(until).toLocaleDateString("sv-SE")}.</p>
          <Link to="/practice" className="mt-4 inline-flex rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90">Börja öva</Link>
        </div>
      ) : loading ? (
        <Loader2 className="mt-6 h-5 w-5 animate-spin" />
      ) : !user ? (
        <div className="mt-6 rounded-2xl border border-border bg-card p-5 text-sm">
          <p>Logga in eller skapa ett konto först, så kopplas licensen till dig.</p>
          <div className="mt-4 flex gap-2">
            <Link to="/login" search={{ next: "/losa-in" }} className="flex-1 rounded-xl bg-primary px-4 py-3 text-center font-semibold text-primary-foreground">Logga in</Link>
            <Link to="/signup" className="flex-1 rounded-xl border border-border px-4 py-3 text-center">Skapa konto</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="AP-XXXX-XXXX"
            autoCapitalize="characters"
            className="w-full rounded-xl border border-input bg-background px-4 py-3 font-mono text-lg tracking-wider focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button disabled={busy || code.trim().length < 8} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Aktivera Premium
          </button>
          <p className="text-xs text-muted-foreground">Inloggad som {user.email}</p>
        </form>
      )}
    </div>
  );
}
